import i18n, { Trans, useTranslation } from "utilities/i18n";
import React, { ReactNode, useState } from "react";
import {
  DescribeCertificateQuery,
  DeviceCertificate,
  DeviceFragment,
  useDescribeCertificateQuery,
  useInstallCertificateMutation,
  useUninstallCertificateMutation
} from "generated/graphql";
import { DeviceHeaderActions, DeviceHeaderWithActions } from "../DeviceHeader";
import * as yup from "yup";
import {
  DISMISS,
  Formik,
  FormikHelpers,
  TextArea,
  useNotification
} from "collections";
import BlockingForm from "collections/Form/BlockingForm";
import { Form } from "collections/Form/Form";
import { DefaultModalForm, useModalDispatch } from "collections/Modal";
import { PasswordInput } from "collections/Form/PasswordInput";
import {
  CancelButton,
  LoadingIndicator,
  SubmitButton,
  FileUploadReader,
  FileUploadResponse
} from "elements";
import { Layout } from "views/Main/Layouts";
import { dateToString, parseIntSecondsDate } from "utilities/datetime";
import { Guard, ProtectedRoute } from "TsRoleGuards";
import { infiniteNestLink, Link } from "polyrouter";
import { UseQueryState } from "urql";
import { PFX_SUPPORTED_FORMATS } from "Constants";

const { ScrollingList } = Layout.Content;

const SETTINGS = {
  PASSWORD: "certificate_password",
  CERTIFICATE: "certificate"
};

const LABELS = {
  PASSWORD: i18n.t("DEVICE_CERTIFICATE_PASSWORD"),
  CERTIFICATE: i18n.t("DEVICE_CERTIFICATE")
};

const RelayCertificateFormSchema = yup.object({
  [SETTINGS.PASSWORD]: yup
    .string()
    .label(LABELS.PASSWORD)
    .required(),
  [SETTINGS.CERTIFICATE]: yup
    .string()
    .label(LABELS.CERTIFICATE)
    .required()
});

const initialValues = {
  [SETTINGS.PASSWORD]: "",
  [SETTINGS.CERTIFICATE]: ""
};

type RelayCertificateForm = yup.InferType<typeof RelayCertificateFormSchema>;

export const RelayDeviceCertificate: React.FC<{ device: DeviceFragment }> = ({
  device
}) => {
  const { t } = useTranslation();
  const { fetching, data } = useDescribeCertificateQuery({
    variables: { deviceId: device.id }
  });
  const [, saveCertificate] = useInstallCertificateMutation();
  const [mode, setMode] = useState<"edit" | "view">("view");
  const [addNotification] = useNotification();

  const hasCert =
    !fetching &&
    !!data?.describeCertificate.subjectCN &&
    !!data?.describeCertificate.expiry;

  const viewForm = "edit" === mode || ("view" === mode && !hasCert);

  const submitForm = async (
    values: RelayCertificateForm,
    helpers: FormikHelpers<RelayCertificateForm>
  ) => {
    const deviceId = device.id;
    const certificate: DeviceCertificate = {
      certificateKey: values.certificate_password,
      certificatePfx: values.certificate
    };

    const { data } = await saveCertificate({ deviceId, certificate });

    if (
      !data ||
      !data.installCertificate ||
      !data.installCertificate.success ||
      data.installCertificate.error
    ) {
      const message =
        data?.installCertificate?.error || t("DEVICE_CERTIFICATE_ERROR");
      addNotification({
        dismiss: DISMISS.DEFAULT,
        id: "certificate_general_error",
        type: "error",
        message: message
      });

      return;
    }

    const { subjectCN } = data.installCertificate;

    const message = t("DEVICE_CERTIFICATE_SUCCESS", { subjectCN });

    addNotification({
      dismiss: DISMISS.DEFAULT,
      id: "certificate_success",
      type: "success",
      message: message
    });

    helpers.resetForm();
    setMode("view");
  };

  let headerActions: ReactNode;
  if (!viewForm) {
    headerActions = (
      <Guard requirement="update:device">
        <Link
          to={infiniteNestLink("/uninstall")}
          className="btn"
          disabled={!device.connected || !hasCert}
        >
          {t("UNINSTALL")}
        </Link>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!device.connected}
          onClick={() => {
            setMode("edit");
          }}
        >
          {t("EDIT")}
        </button>
      </Guard>
    );
  }

  if (viewForm) {
    headerActions = (
      <DeviceHeaderActions
        device={device}
        showConfirm={false}
        unsavedChanges={0}
        onDiscard={() => {
          setMode("view");
        }}
        discardKey="CANCEL"
        // when no cert, the form is displayed by default, and cancel should only be available if form is dirty.
        // when there IS a cert, the form is ONLY displayed when attempting to edit, and cancel should always be available.
        discardIfNotDirty={hasCert}
      />
    );
  }

  return (
    <>
      <Formik
        name="RelaySettings"
        initialValues={initialValues}
        validationSchema={RelayCertificateFormSchema}
        onSubmit={submitForm}
      >
        {({ dirty, values, setValues }) => (
          <BlockingForm unsavedChanges={dirty} allowedSubRoutes={["/settings"]}>
            <Form className="form pb-2">
              <DeviceHeaderWithActions
                device={device}
                pageTitle={
                  <>
                    {`${device?.hardwareModel} |`}
                    <p className={"d-inline-block device-header-name"}>
                      {device?.name || device?.room?.name || device?.macAddress}
                    </p>
                  </>
                }
                actions={headerActions}
              />
              <ScrollingList>
                <div className="card title-card">{t("CERTIFICATE")}</div>
                {viewForm && (
                  <div className="details grid form">
                    {!hasCert && (
                      <div className="pb-2 card-title-indent">
                        <Trans i18nKey="DEVICE_RELAY_HELPER_TEXT">
                          Relays default to a self-signed certificate.
                          <br />
                          To install a custom certificate, complete the fields
                          below and click "Apply".
                        </Trans>
                      </div>
                    )}
                    <PasswordInput
                      name={SETTINGS.PASSWORD}
                      label={LABELS.PASSWORD}
                    />
                    <div className="form-group">
                      <label
                        className="col-4 text-right v-top"
                        htmlFor={SETTINGS.CERTIFICATE + "-textarea"}
                      >
                        {LABELS.CERTIFICATE}:
                      </label>
                      <div className="col-5">
                        <TextArea
                          name={SETTINGS.CERTIFICATE}
                          className="d-none"
                        />
                        <FileUploadReader
                          onUpload={(response: FileUploadResponse) => {
                            setValues({
                              ...values,
                              certificate: response.content
                            });
                          }}
                          readyText={t("RELAY_CERTIFICATE_UPLOAD_TEXT")}
                          draggingText={t("DROP_FILE")}
                          errorText={t("RELAY_CERTIFICATE_UPLOAD_ERROR")}
                          mimeTypes={PFX_SUPPORTED_FORMATS}
                          returnAs="base64"
                        />
                      </div>
                    </div>
                  </div>
                )}
                {!viewForm && (
                  <RelayViewCertificate certQuery={{ fetching, data }} />
                )}
              </ScrollingList>
            </Form>
          </BlockingForm>
        )}
      </Formik>
      <ProtectedRoute
        nest
        path="/uninstall"
        requirement="update:device"
        render={() => (
          <UninstallCertificateModal
            deviceId={device.id}
            closeModal={() => {
              setMode("view");
            }}
          />
        )}
      />
    </>
  );
};
const RelayViewCertificate: React.FC<{
  certQuery: Partial<UseQueryState<DescribeCertificateQuery, {}>>;
}> = ({ certQuery }) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState<boolean>(false);
  const { data, fetching } = certQuery;

  if (fetching) {
    return <LoadingIndicator />;
  }

  const { describeCertificate: cert } = data || { describeCertificate: null };

  // this actually should never happen
  if (!cert || (!cert.expiry && !cert.subjectCN)) {
    return null;
  }

  // sans comes in as a string: "DNS:stage.lens.poly.com, DNS:www.stage.lens.poly"
  // split by comma, strip DNS from start
  const sans = cert.sans
    ? cert.sans.split(",").map(san => {
        return san.replace(/^\s?dns:\s?/i, "");
      })
    : [];

  // want the first 10 to display always, and the rest only when click "view all"
  const displaySans = {
    firstTen: sans.slice(0, 9),
    rest: sans.slice(9)
  };

  // the date comes back as seconds after epoch.
  const expireDate = !cert.expiry
    ? "-"
    : dateToString(parseIntSecondsDate(cert.expiry));

  return (
    <div className="grid">
      <label className="col-4 text-right">{t("EXPIRATION")}:</label>
      <div className="col-8">{expireDate}</div>
      <label className="col-4 text-right">{t("ISSUED_BY")}:</label>
      <div className="col-8">{cert.issuerCN}</div>
      <label className="col-4 text-right">{t("COMMON_NAME")}:</label>
      <div className="col-8">{cert.subjectCN}</div>
      <label className="col-4 text-right v-top">
        {t("SUBJECT_ALTERNATIVE_NAMES")}
      </label>
      <div className="col-8">
        {!displaySans.firstTen.length && t("NONE")}
        {displaySans.firstTen.map(san => (
          <div key={san}>{san}</div>
        ))}
        {!!displaySans.rest.length && !showAll && (
          <div>
            <span
              role="link"
              className="btn btn-link"
              onClick={() => {
                setShowAll(true);
              }}
            >
              {t("DEVICE_CERTIFICATE_VIEW_MORE_SANS", {
                count: displaySans.rest.length
              })}
            </span>
          </div>
        )}
        {!!displaySans.rest.length &&
          showAll &&
          displaySans.rest.map(san => <div key={san}>{san}</div>)}
      </div>
    </div>
  );
};

export const UninstallCertificateModal: React.FC<{
  deviceId: string;
  closeModal: () => void;
}> = ({ deviceId, closeModal }) => {
  const { t } = useTranslation();
  const returnTo = useModalDispatch();
  const [, uninstallCertificate] = useUninstallCertificateMutation();
  const [addNotification] = useNotification();

  const handleCloseModal = () => {
    closeModal();
    returnTo();
  };

  const handleSubmit = async () => {
    const { data } = await uninstallCertificate({
      deviceId
    });

    if (data?.uninstallCertificate.success) {
      addNotification({
        id: "uninstallCertificate",
        message: t("DEVICE_CERTIFICATE_UNINSTALL_SUCCESS"),
        type: "success",
        dismiss: DISMISS.DEFAULT,
        persistThroughNav: true
      });
    }

    if (
      !data?.uninstallCertificate.success ||
      data?.uninstallCertificate.error
    ) {
      addNotification({
        id: "uninstallCertificate",
        message: t("DEVICE_CERTIFICATE_UNINSTALL_ERROR"),
        type: "error",
        dismiss: DISMISS.DEFAULT,
        persistThroughNav: true
      });
    }

    handleCloseModal();
  };

  return (
    <DefaultModalForm
      title={t("UNINSTALL_CERTIFICATE")}
      onSubmit={handleSubmit}
      name="uninstallCertificate"
    >
      {({ isSubmitting }) => (
        <>
          <div data-testid="confirmation">
            {t("CONFIRM_UNINSTALL_RELAY_CERTIFICATE")}
          </div>
          <div className="btn-container">
            <CancelButton onClick={handleCloseModal} />
            <SubmitButton
              isSubmitting={isSubmitting}
              labelKey="UNINSTALL"
              submittingLabelKey="UNINSTALLING"
            />
          </div>
        </>
      )}
    </DefaultModalForm>
  );
};
